type SentryModule = typeof import('@sentry/react');
type CaptureContext = Parameters<SentryModule['captureException']>[1];

type SentryWindow = Window & { __sentryInitialized?: boolean };

let sentryPromise: Promise<SentryModule | null> | undefined;

export function initSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn || sentryPromise) return;

  (window as SentryWindow).__sentryInitialized = true;
  sentryPromise = import('@sentry/react')
    .then((Sentry) => {
      Sentry.init({
        dsn,
        environment: import.meta.env.MODE,
        integrations: [
          Sentry.browserTracingIntegration(),
          // Replays are only recorded on error (replaysSessionSampleRate: 0),
          // but an error can happen on any portal page. Mask everything: the
          // client / employee / member portals render PII and contract data.
          Sentry.replayIntegration({ maskAllText: true, blockAllMedia: true }),
        ],
        // @sentry/react 11 removed `sendDefaultPii`, and an unset
        // `dataCollection` collects everything (user, cookies, headers, bodies,
        // query params, stack-frame variables). Switch every category off: the
        // most private equivalent of v10's `sendDefaultPii: false`. Query
        // params stay off because invite / unsubscribe links carry tokens.
        // `frameContextLines` (source lines around a frame) is code, not user
        // data, and keeps its default. Session Replay is governed by the
        // masking options above, not by this block.
        // https://docs.sentry.io/platforms/javascript/guides/react/migration/v10-to-v11/
        dataCollection: {
          userInfo: false,
          cookies: false,
          httpHeaders: { request: false, response: false },
          httpBodies: [],
          urlQueryParams: false,
          genAI: { inputs: false, outputs: false },
          databaseQueryData: false,
          graphQL: { document: false, variables: false },
          queues: false,
          stackFrameVariables: false,
        },
        tracesSampleRate: 0.1,
        replaysSessionSampleRate: 0,
        replaysOnErrorSampleRate: 1.0,
        denyUrls: [
          /^chrome-extension:\/\//i,
          /^moz-extension:\/\//i,
          /^safari-extension:\/\//i,
          /^ms-browser-extension:\/\//i,
          /^blob:/i,
          /^data:/i,
        ],
        ignoreErrors: [
          'Cambria Math',
          'downloadable font',
          'Extension context invalidated',
          'Failed to read the sessionStorage property',
          'Failed to read the localStorage property',
          'Non-Error promise rejection captured',
          'ResizeObserver loop completed with undelivered notifications',
          'ResizeObserver loop limit exceeded',
          'The operation is insecure',
          'window.webkitStorageInfo',
          'webkitStorageInfo',
          'compacted',
          'compact.js',
        ],
      });
      return Sentry;
    })
    .catch((error: unknown) => {
      (window as SentryWindow).__sentryInitialized = false;
      console.error('[Sentry] Failed to initialize:', error);
      return null;
    });
}

export async function captureException(error: unknown, context?: CaptureContext): Promise<void> {
  // Errors before consent/init still reach the SDK's local no-op client while
  // keeping the observability chunk off every successful startup path.
  const Sentry = await (sentryPromise ?? import('@sentry/react')).catch(() => null);
  Sentry?.captureException(error, context);
}
